import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRight, Loader2, MailCheck, FileText, Package, Users, BarChart3,
  Receipt, Wallet, BookOpen, ScrollText, Fingerprint,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import SiteHeader, { SerpLogo } from "@/components/SiteHeader";
import { isPasskeySupported, checkPasskeyAvailable, loginWithPasskey } from "@/lib/passkey";

const HIGHLIGHTS = [
  { icon: BookOpen, name: "Double-entry ledger", desc: "Chart of Accounts + 28 groups" },
  { icon: ScrollText, name: "Full voucher suite", desc: "Sales · Purchase · Payment · Journal…" },
  { icon: FileText, name: "GST / VAT Invoicing", desc: "CGST/SGST/IGST or VAT auto-calc" },
  { icon: Package, name: "Inventory", desc: "HSN/SAC, low-stock, godowns" },
  { icon: BarChart3, name: "Statutory reports", desc: "Trial Balance · P&L · Balance Sheet" },
  { icon: Wallet, name: "Multi-Currency", desc: "INR · USD · AED · EUR · GBP + live FX" },
];

export default function AuthPage({ mode = "login" }) {
  const [email, setEmail] = useState(() => {
    try { return localStorage.getItem("serp_remember_email") || ""; } catch { return ""; }
  });
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [company, setCompany] = useState("");
  const [remember, setRemember] = useState(() => {
    try { return localStorage.getItem("serp_remember_email") !== null; } catch { return false; }
  });
  const [loading, setLoading] = useState(false);
  const [pending, setPending] = useState(null);
  const [pkLoading, setPkLoading] = useState(false);
  const [pkAvailable, setPkAvailable] = useState(false);
  const { login, loginWithSession, register, formatApiErrorDetail } = useAuth();
  const navigate = useNavigate();
  const isRegister = mode === "register";

  // Handle `?gated=1` — set when the axios interceptor forcibly logged a
  // pending user out because Super Admin flipped their status mid-session.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("gated") === "1") {
      setPending({
        email: email || "",
        name: (email || "").split("@")[0] || "there",
        approval_email_to: "info@sresources.co",
        approval_email_sent: true,
        message: "Your account is pending approval by the Shanvi Resources Super Administrator. You will receive an email once your access is activated.",
      });
    }
  }, []);

  // Detect passkey availability whenever the identifier changes.
  useEffect(() => {
    if (isRegister) { setPkAvailable(false); return; }
    if (!isPasskeySupported()) { setPkAvailable(false); return; }
    if (!email) { setPkAvailable(false); return; }
    let cancelled = false;
    const t = setTimeout(async () => {
      const ok = await checkPasskeyAvailable(email);
      if (!cancelled) setPkAvailable(ok);
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [email, isRegister]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isRegister) {
        const res = await register(email, password, name, company);
        setPending({
          email: res.email, name: res.name,
          approval_email_to: res.approval_email_to || "info@sresources.co",
          approval_email_sent: !!res.approval_email_sent,
        });
      } else {
        await login(email, password);
        // Remember me: persist email (never password) across sessions.
        try {
          if (remember) localStorage.setItem("serp_remember_email", email);
          else localStorage.removeItem("serp_remember_email");
        } catch { /* storage disabled */ }
        navigate("/app/dashboard");
      }
    } catch (err) {
      const detail = formatApiErrorDetail(err?.response?.data?.detail);
      const status = err?.response?.status;
      // A 403 with a "pending approval" body means the user exists but is
      // gated by the Super Admin — surface a proper info panel, not a toast.
      if (status === 403 && detail && /pending approval/i.test(detail)) {
        setPending({
          email,
          name: name || email.split("@")[0],
          approval_email_to: "info@sresources.co",
          approval_email_sent: true,
          message: detail,
        });
      } else {
        toast.error(detail || "Authentication failed");
      }
    } finally { setLoading(false); }
  };

  const signInWithPasskey = async () => {
    if (!email) { toast.error("Enter your email or username first"); return; }
    setPkLoading(true);
    try {
      const data = await loginWithPasskey(email);
      loginWithSession(data);
      try {
        if (remember) localStorage.setItem("serp_remember_email", email);
        else localStorage.removeItem("serp_remember_email");
      } catch { /* storage disabled */ }
      toast.success("Signed in with passkey");
      navigate("/app/dashboard");
    } catch (err) {
      const msg = formatApiErrorDetail(err?.response?.data?.detail);
      const status = err?.response?.status;
      const errName = err?.name || "";
      if (status === 403 && msg && /pending approval/i.test(msg)) {
        setPending({
          email, name: email.split("@")[0],
          approval_email_to: "info@sresources.co",
          approval_email_sent: true,
          message: msg,
        });
      } else if (errName === "NotAllowedError" || errName === "AbortError") {
        toast.error("Passkey prompt was cancelled");
      } else {
        toast.error(msg || err?.message || "Passkey sign-in failed");
      }
    } finally { setPkLoading(false); }
  };

  return (
    <div className="min-h-screen flex flex-col bg-white overflow-x-hidden">
      <SiteHeader />
      <div className="flex flex-col lg:flex-row flex-1" style={{ minHeight: "calc(100vh - 72px)" }}>
        {/* Brand panel — sits on top on mobile (single column), left on desktop */}
        <aside
          className="lg:w-[42%] flex flex-col justify-center px-6 sm:px-10 lg:px-12 py-8 lg:py-12 relative overflow-hidden bg-[#FFEDE0]"
          data-testid="login-brand-panel"
        >
          <div className="relative z-10 max-w-md mx-auto lg:mx-0 w-full">
            <SerpLogo height={44} testid="auth-brand-logo"/>
            <div className="text-[10px] sm:text-[11px] font-mono-data uppercase tracking-widest text-[#FF5500] mt-4 lg:mt-6 mb-2">
              Global ERP · Accounting Suite
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight mb-3 leading-tight text-[#1C1C1C]">
              Tally-grade accounting.<br/>
              Global by default.
            </h1>
            <p className="text-sm text-[#4B5563] leading-relaxed mb-6 lg:mb-8">
              Multi-currency, multi-country, multi-company. One workspace to run your business books.
            </p>
            {/* Highlights — hidden on mobile to keep the form high on the screen */}
            <div className="hidden lg:grid grid-cols-2 gap-4">
              {HIGHLIGHTS.map(({ icon: Icon, name, desc }) => (
                <div key={name} className="flex gap-2">
                  <Icon size={16} className="text-[#FF5500] mt-0.5 shrink-0"/>
                  <div>
                    <div className="text-sm font-semibold text-[#1C1C1C]">{name}</div>
                    <div className="text-[11px] text-[#6B7280] leading-snug">{desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </aside>

        {/* Form panel */}
        <div className="flex-1 flex flex-col justify-center items-center px-6 sm:px-8 py-10 lg:py-0 bg-white">
          {pending ? (
            <div className="max-w-md w-full" data-testid="auth-pending-screen">
              <div className="w-14 h-14 rounded-lg bg-[#FFEDE0] flex items-center justify-center mb-5">
                <MailCheck size={26} className="text-[#FF5500]" />
              </div>
              <div className="overline">Account pending approval</div>
              <h1 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight mt-1 mb-3 text-[#1C1C1C]">
                Your account is under review.
              </h1>
              <p className="text-sm text-[#4B5563] leading-relaxed mb-6" data-testid="auth-pending-message">
                {pending.message || (
                  <>
                    Thanks {pending.name}. Your account is pending approval by the{" "}
                    <span className="font-semibold text-[#1C1C1C]">Shanvi Resources Super Administrator</span>.
                    You will receive an email at{" "}
                    <span className="font-mono-data text-[#1C1C1C]">{pending.email}</span>{" "}once your access is activated.
                  </>
                )}
              </p>
              <Link to="/login" className="btn-primary" data-testid="auth-pending-login-btn">
                <ArrowRight size={14} /> Go to sign in
              </Link>
            </div>
          ) : (
            <div className="max-w-md w-full">
              <div className="overline">{isRegister ? "New account" : "Welcome back"}</div>
              <h1 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight mt-1 mb-2 text-[#1C1C1C]">
                {isRegister ? "Create your account" : "Sign in to SERP"}
              </h1>
              <p className="text-sm text-[#4B5563] mb-6 sm:mb-8">
                {isRegister
                  ? "Start invoicing, tracking inventory & running GST reports in minutes."
                  : "Continue managing your books — vouchers, ledgers, reports, all in one place."}
              </p>
              <form onSubmit={submit} className="space-y-4">
                {isRegister && (
                  <>
                    <div><label className="label-field">Full name</label>
                      <input data-testid="auth-name" value={name} onChange={(e) => setName(e.target.value)} className="input-clean" placeholder="Jane Sharma" required autoComplete="name"/></div>
                    <div><label className="label-field">Company</label>
                      <input data-testid="auth-company" value={company} onChange={(e) => setCompany(e.target.value)} className="input-clean" placeholder="Your Company Pvt Ltd" autoComplete="organization"/></div>
                  </>
                )}
                <div><label className="label-field">Email or Username</label>
                  <input data-testid="auth-email" type="text" value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="input-clean"
                    placeholder="you@company.com or SERP"
                    required autoComplete="username"
                    inputMode="email" autoCapitalize="off" autoCorrect="off"/></div>
                <div><label className="label-field">Password</label>
                  <input data-testid="auth-password" type="password" value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="input-clean" placeholder="At least 6 characters"
                    required minLength={6}
                    autoComplete={isRegister ? "new-password" : "current-password"}/></div>
                {!isRegister && (
                  <label className="flex items-center gap-2 select-none cursor-pointer text-sm text-[#4B5563]" data-testid="auth-remember-wrap">
                    <input type="checkbox" data-testid="auth-remember"
                      checked={remember} onChange={(e) => setRemember(e.target.checked)}
                      className="w-4 h-4 accent-[#FF5500] cursor-pointer"/>
                    <span>Remember me on this device</span>
                  </label>
                )}
                <button data-testid="auth-submit" type="submit" disabled={loading}
                  className="btn-primary w-full justify-center py-3 text-base">
                  {loading ? <Loader2 size={14} className="animate-spin" /> : <ArrowRight size={14} />}
                  {isRegister ? "Create account" : "Sign in"}
                </button>
                {!isRegister && isPasskeySupported() && (
                  <>
                    <div className="relative py-1">
                      <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-[#E5E7EB]"></div>
                      </div>
                      <div className="relative flex justify-center">
                        <span className="bg-white px-3 text-[11px] text-[#94A3B8] uppercase tracking-widest font-mono-data">
                          or
                        </span>
                      </div>
                    </div>
                    <button
                      type="button"
                      data-testid="auth-passkey-btn"
                      onClick={signInWithPasskey}
                      disabled={pkLoading || !email}
                      title={pkAvailable ? "Sign in with your registered passkey" : "Use Face ID / Touch ID / Fingerprint"}
                      className="w-full justify-center py-3 text-base flex items-center gap-2 rounded-lg border-2 border-[#0F172A] text-[#0F172A] font-semibold hover:bg-[#0F172A] hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{ minHeight: 48 }}
                    >
                      {pkLoading
                        ? <Loader2 size={16} className="animate-spin" />
                        : <Fingerprint size={18} />}
                      Sign in with passkey
                      {pkAvailable && (
                        <span className="ml-1 text-[10px] font-mono-data text-[#16A34A]" data-testid="passkey-available-badge">
                          ●
                        </span>
                      )}
                    </button>
                  </>
                )}
              </form>
              <div className="mt-6 sm:mt-8 text-sm text-[#4B5563]">
                {isRegister ? (
                  <>Already have an account? <Link to="/login" className="text-[#FF5500] font-semibold" data-testid="link-signin">Sign in</Link></>
                ) : (
                  <>Need an account? <Link to="/register" className="text-[#FF5500] font-semibold" data-testid="link-register">Create one</Link></>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
